import * as React from "react";
import { CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * One platform's numbers, as a fixed `36px 1fr auto` row: platform code,
 * followers, engagement rate.
 *
 * The code and the engagement rate are mono; the follower count is the only
 * thing set in the sans face, because it is the figure a brand scans for and
 * the mono face makes it harder to read at a glance.
 *
 * The tick beside the platform code is not decoration — it means that account's
 * numbers came from an OAuth grant rather than from someone typing them in.
 * Provenance is never inferred and never flattering; pass what the data says.
 */
export interface MetricRowProps extends React.HTMLAttributes<HTMLDivElement> {
  /** `IG` / `TT` / `YT`. */
  platform: string;
  followers: React.ReactNode;
  engagement?: React.ReactNode;
  verified?: boolean;
  /** Sitting on a creator portrait rather than on a white card. */
  onImage?: boolean;
}

export function MetricRow({
  platform,
  followers,
  engagement,
  verified = false,
  onImage = true,
  className,
  ...props
}: MetricRowProps) {
  return (
    <div
      className={cn(
        "grid grid-cols-[36px_1fr_auto] items-center gap-2 border-t py-1",
        onImage ? "border-on-image-rule" : "border-neutral-100",
        className,
      )}
      {...props}
    >
      <span
        className={cn(
          "adv-mono inline-flex items-center gap-0.5 text-[10.5px] font-bold",
          onImage ? "text-on-image-muted" : "text-neutral-400",
        )}
      >
        {platform}
        {verified && (
          <CheckCircle2
            className={cn("size-[9px]", onImage ? "text-steel-200" : "text-steel-500")}
            strokeWidth={3}
          />
        )}
      </span>

      <span
        className={cn(
          "text-card-body font-bold tracking-tight",
          onImage ? "text-white" : "text-neutral-900",
        )}
      >
        {followers}
      </span>

      {engagement && (
        <span
          className={cn(
            "adv-mono text-chip font-medium",
            onImage ? "text-on-image-faint" : "text-neutral-500",
          )}
        >
          {engagement}
        </span>
      )}
    </div>
  );
}
